export type RecipeAssistedEntryMode = "cards" | "compact";

/** Shared inputs for assisted start cards and compact method buttons. */
export type RecipeAssistedEntryActions = {
  showLink: boolean;
  onImportFromLink?: () => void;
  onTranscribePhoto?: () => void;
  onGenerateWithAi?: () => void;
};

export type RecipeAssistedMethodKey = "link" | "photo" | "ai";

export type RecipeAssistedStartCardConfig = {
  key: RecipeAssistedMethodKey;
  title: string;
  description: string;
  onClick: () => void;
};

export type RecipeAssistedMethodButtonConfig = {
  key: RecipeAssistedMethodKey;
  label: string;
  ariaLabel: string;
  onClick: () => void;
};

/** Empty list → start cards; one or more ingredients → compact toolbar controls. */
export const getRecipeAssistedEntryMode = (ingredientCount: number): RecipeAssistedEntryMode =>
  ingredientCount === 0 ? "cards" : "compact";

/** Whether any assisted entry method is available. */
export const hasRecipeAssistedEntryActions = (actions: RecipeAssistedEntryActions): boolean =>
  getRecipeAssistedMethodKeys(actions).length > 0;

/** Which compact method buttons are visible. */
export const getRecipeAssistedMethodKeys = ({
  showLink,
  onImportFromLink,
  onTranscribePhoto,
  onGenerateWithAi,
}: RecipeAssistedEntryActions): RecipeAssistedMethodKey[] => {
  const keys: RecipeAssistedMethodKey[] = [];
  if (showLink && onImportFromLink) {
    keys.push("link");
  }
  if (onTranscribePhoto) {
    keys.push("photo");
  }
  if (onGenerateWithAi) {
    keys.push("ai");
  }
  return keys;
};

const methodButtonCopy: Record<RecipeAssistedMethodKey, { label: string; ariaLabel: string }> = {
  link: { label: "Link", ariaLabel: "Import recipe from link" },
  photo: { label: "Photo", ariaLabel: "Transcribe recipe from photo" },
  ai: { label: "AI", ariaLabel: "Generate recipe with AI" },
};

/** Compact method buttons with label / aria-label / handler. */
export const getRecipeAssistedMethodButtonConfigs = (
  actions: RecipeAssistedEntryActions
): RecipeAssistedMethodButtonConfig[] => {
  const { onImportFromLink, onTranscribePhoto, onGenerateWithAi } = actions;
  const onClickByKey: Partial<Record<RecipeAssistedMethodKey, () => void>> = {
    link: onImportFromLink,
    photo: onTranscribePhoto,
    ai: onGenerateWithAi,
  };

  return getRecipeAssistedMethodKeys(actions).flatMap(key => {
    const onClick = onClickByKey[key];
    if (!onClick) {
      return [];
    }
    return [{ key, onClick, ...methodButtonCopy[key] }];
  });
};

/** Which start cards are visible, with title/description copy. */
export const getRecipeAssistedStartCardConfigs = ({
  showLink,
  onImportFromLink,
  onTranscribePhoto,
  onGenerateWithAi,
}: RecipeAssistedEntryActions): RecipeAssistedStartCardConfig[] => {
  const cards: RecipeAssistedStartCardConfig[] = [];

  if (showLink && onImportFromLink) {
    cards.push({
      key: "link",
      title: "Import from a link",
      description: "Paste a URL from a recipe site or blog.",
      onClick: onImportFromLink,
    });
  }

  if (onTranscribePhoto) {
    cards.push({
      key: "photo",
      title: "Transcribe a photo",
      description: "Upload a JPEG or PNG of a recipe.",
      onClick: onTranscribePhoto,
    });
  }

  if (onGenerateWithAi) {
    cards.push({
      key: "ai",
      title: "Generate with AI",
      description: "Describe a dish and serving targets.",
      onClick: onGenerateWithAi,
    });
  }

  return cards;
};
